import React, { useState } from 'react';

/**
 * Full-page interstitial for children without a birth year.
 * The guardian must provide a birth year (required) and may optionally
 * provide an exact birth date for more precise age tracking.
 *
 * Props:
 *   memberId   - The family member UUID
 *   onComplete - callback({ birth_year, birth_date? }) after submission
 */
function MissingBirthYearPrompt({ memberId, onComplete }) {
  const currentYear = new Date().getFullYear();
  const [birthYear, setBirthYear] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const year = parseInt(birthYear, 10);
    if (isNaN(year) || year < 1900 || year > currentYear) {
      setError(`Please enter a valid year between 1900 and ${currentYear}.`);
      return;
    }

    setSubmitting(true);
    try {
      const payload = { birth_year: year };
      if (birthDate) {
        payload.birth_date = birthDate;
      }
      await onComplete(payload);
    } catch (err) {
      setError('Something went wrong. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-6 sm:p-8">
        <div className="text-center mb-6">
          <span className="text-4xl" role="img" aria-label="calendar">{'\uD83D\uDCC5'}</span>
          <h2 className="font-serif text-2xl sm:text-3xl text-[#556B2F] font-bold mt-3">
            When were they born?
          </h2>
          <p className="text-gray-600 mt-2 text-base leading-relaxed">
            We need a birth year to set up the right chapters for their age.
            You can also add the exact date if you know it.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Birth Year (required) */}
          <div>
            <label htmlFor="birth-year" className="block text-sm font-medium text-gray-700 mb-1">
              Birth Year <span className="text-red-500">*</span>
            </label>
            <input
              id="birth-year"
              type="number"
              min="1900"
              max={currentYear}
              value={birthYear}
              onChange={(e) => setBirthYear(e.target.value)}
              placeholder={`e.g. ${currentYear - 3}`}
              required
              className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#556B2F] focus:border-[#556B2F] outline-none transition-colors"
            />
          </div>

          {/* Birth Date (optional) */}
          <div>
            <label htmlFor="birth-date" className="block text-sm font-medium text-gray-700 mb-1">
              Exact Birth Date <span className="text-gray-400">(optional)</span>
            </label>
            <input
              id="birth-date"
              type="date"
              value={birthDate}
              onChange={(e) => setBirthDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#556B2F] focus:border-[#556B2F] outline-none transition-colors"
            />
          </div>

          {error && (
            <p className="text-red-600 text-sm">{error}</p>
          )}

          <button
            type="submit"
            disabled={submitting || !birthYear}
            className="w-full py-3 px-6 rounded-lg bg-[#556B2F] text-white font-semibold text-lg hover:bg-[#4A5D23] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {submitting ? 'Saving...' : 'Continue'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default MissingBirthYearPrompt;
